/**
 * GivingLetterDialog — a member's year of giving, ready to print.
 *
 * One letter from all three books: offerings the office recorded, gifts given
 * online, and payments receipted against a pledge. Currencies are listed
 * separately and never added together, money still waiting on an exchange rate
 * is shown apart, and a reversed payment appears nowhere but is counted in the
 * footnote so the letter can be trusted.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Printer } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import api from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const THIS_YEAR = new Date().getFullYear();

const money = (n, ccy) => `${ccy} ${Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** The print window shares this origin, so everything typed by a person is
 *  escaped before it is written into it. */
const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[ch]));

const printLetter = (d) => {
  const win = window.open('', '_blank', 'width=700,height=900');
  if (!win) return;
  const rows = (d.gifts || []).map((g) => `
    <tr>
      <td>${esc(g.date)}</td>
      <td>${esc(g.description || '—')}<span class="muted"> · ${esc(g.source)}</span></td>
      <td>${esc(g.reference || '')}</td>
      <td style="text-align:right">${esc(money(g.amount, g.currency))}</td>
    </tr>`).join('');
  const totals = Object.entries(d.by_currency || {}).map(([ccy, t]) => `
    <tr class="total"><td colspan="3"><strong>Total received in ${esc(ccy)}
      <span class="muted">(${t.gifts} gift${t.gifts === 1 ? '' : 's'})</span></strong></td>
      <td style="text-align:right"><strong>${esc(money(t.total, ccy))}</strong></td></tr>`).join('');
  const held = Object.entries(d.held_by_currency || {}).map(([ccy, amount]) => `
    <li>${esc(money(amount, ccy))} received but not yet converted — awaiting an
        authorised exchange rate, so it is not in the totals above.</li>`).join('');
  win.document.write(`<!doctype html><html><head><title>Giving ${esc(d.year)} — ${esc(d.giver.name)}</title>
    <style>
      body{font-family:Georgia,serif;color:#0f172a;margin:40px;line-height:1.5}
      h1{font-size:20px;margin:0 0 4px}
      .muted{color:#64748b;font-size:11px}
      .issuer{font-size:12px;color:#475569;margin-bottom:22px}
      table{width:100%;border-collapse:collapse;font-size:13px;margin-top:10px}
      th{text-align:left;border-bottom:1px solid #cbd5e1;padding:6px 4px;font-size:10px;
         text-transform:uppercase;letter-spacing:.06em;color:#64748b}
      td{padding:6px 4px;border-bottom:1px solid #eef2f6;vertical-align:top}
      tr.total td{border-top:2px solid #0f172a;border-bottom:none;padding-top:9px}
      ul{font-size:12px;color:#334155;padding-left:18px}
      .foot{margin-top:26px;font-size:11px;color:#64748b}
    </style></head><body>
    <h1>Statement of giving · ${esc(d.year)}</h1>
    <p class="muted">${esc(d.giver.name)}${d.giver.email ? ` · ${esc(d.giver.email)}` : ''}</p>
    <p class="issuer"><strong>${esc(d.issuer?.name || '')}</strong>
      ${d.issuer?.address ? `<br/>${esc(d.issuer.address)}` : ''}
      ${d.issuer?.email ? `<br/>${esc(d.issuer.email)}` : ''}</p>
    <p>With thanks for your giving during ${esc(d.year)}. The gifts recorded
       against your name are set out below.</p>
    <table><thead><tr><th>Date</th><th>Given towards</th><th>Receipt</th>
      <th style="text-align:right">Amount</th></tr></thead>
      <tbody>${rows || '<tr><td colspan="4">No gifts were recorded in this year.</td></tr>'}${totals}</tbody></table>
    ${held ? `<p class="muted" style="margin-top:18px">Also received:</p><ul>${held}</ul>` : ''}
    <p class="foot">${esc(d.note || '')}
      ${d.reversed_excluded ? `<br/>${esc(d.reversed_excluded)} reversed payment(s) are excluded.` : ''}
      <br/>Issued ${esc((d.generated_at || '').slice(0, 10))}. This is a record of
      gifts received, not a tax determination.</p>
    </body></html>`);
  win.document.close();
  win.focus();
  win.print();
};

export const GivingLetterDialog = ({ churchId, memberId, name, mine = false, onClose }) => {
  const [year, setYear] = useState(String(THIS_YEAR));

  const { data, isLoading, error } = useQuery({
    queryKey: ['giving-letter', mine ? 'me' : `${churchId}:${memberId}`, year],
    queryFn: async () => {
      if (mine) {
        const { getToken } = await import('@/lib/authToken');
        const r = await fetch(`${API_URL}/api/member/giving/letter?year=${year}`,
                              { headers: { Authorization: `Bearer ${getToken()}` } });
        if (!r.ok) throw new Error('unavailable');
        return r.json();
      }
      const r = await api.get(`/church/${churchId}/members/${memberId}/giving-letter`,
                              { params: { year } });
      return r.data;
    },
    enabled: mine || !!(churchId && memberId),
    retry: false,
  });

  const currencies = Object.entries(data?.by_currency || {});

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="giving-letter-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">
            Year-end giving letter{name ? ` · ${name}` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between gap-3">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-36" data-testid="letter-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[THIS_YEAR, THIS_YEAR - 1, THIS_YEAR - 2, THIS_YEAR - 3].map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" disabled={!data}
                  onClick={() => data && printLetter(data)}
                  data-testid="letter-print">
            <Printer className="mr-1.5 h-4 w-4" />Print the letter
          </Button>
        </div>

        <div className="max-h-[60vh] space-y-3 overflow-y-auto">
          {isLoading ? (
            <p className="flex items-center gap-2 p-4 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />Gathering the year…
            </p>
          ) : error || !data ? (
            <p className="p-3 text-sm text-rose-600" data-testid="letter-error">
              This letter could not be prepared. Please try again.
            </p>
          ) : (
            <>
              {currencies.length ? (
                <div className="grid gap-2 sm:grid-cols-2" data-testid="letter-totals">
                  {currencies.map(([ccy, t]) => (
                    <div key={ccy} className="rounded-lg border border-slate-200 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                        {ccy}
                      </p>
                      <p className="text-lg font-semibold text-slate-900">
                        {money(t.total, ccy)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {t.gifts} gift{t.gifts === 1 ? '' : 's'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="p-3 text-sm text-slate-500" data-testid="letter-empty">
                  Nothing was recorded against this name in {year}.
                </p>
              )}

              {Object.keys(data.held_by_currency || {}).length ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
                     data-testid="letter-held">
                  {Object.entries(data.held_by_currency).map(([ccy, amount]) => (
                    <p key={ccy}>
                      {money(amount, ccy)} received and receipted, but awaiting an
                      authorised exchange rate — shown separately, never estimated.
                    </p>
                  ))}
                </div>
              ) : null}

              {data.gifts?.length ? (
                <table className="w-full text-sm" data-testid="letter-gifts">
                  <thead>
                    <tr className="border-b text-[10px] uppercase tracking-wide text-slate-500">
                      <th className="px-2 py-2 text-left">Date</th>
                      <th className="px-2 py-2 text-left">Given towards</th>
                      <th className="px-2 py-2 text-left">Receipt</th>
                      <th className="px-2 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.gifts.map((g, i) => (
                      <tr key={`${g.date}-${i}`} className="border-b last:border-0"
                          data-testid={`letter-gift-${i}`}>
                        <td className="px-2 py-2 text-slate-600">{g.date}</td>
                        <td className="px-2 py-2 capitalize">
                          {g.description}
                          <span className="ml-1 text-xs text-slate-400">{g.source}</span>
                        </td>
                        <td className="px-2 py-2 font-mono text-xs text-slate-500">
                          {g.reference || '—'}
                        </td>
                        <td className="px-2 py-2 text-right font-medium">
                          {money(g.amount, g.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}

              {data.reversed_excluded ? (
                <p className="text-xs text-slate-400" data-testid="letter-reversed">
                  {data.reversed_excluded} reversed payment
                  {data.reversed_excluded === 1 ? ' is' : 's are'} excluded from this letter.
                </p>
              ) : null}
              <p className="text-xs text-slate-400">{data.note}</p>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GivingLetterDialog;
